import { FC, useContext, useEffect } from 'react';

import {
  BackwardIcon,
  Button,
  H1,
  H2,
  H3,
  Input,
  NumberInput,
  Preloader,
  Toggle,
} from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { NDSSelect } from 'components';
import { useServicesRequests } from 'hooks';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import { openConfirmDialog } from '../../services/Confirmation';
import notificationService from '../../services/notificationService';
import { DEFAULT_VALUES } from './constants';
import { IFormValues } from './interfaces';
import s from './Service.module.scss';

export const Service: FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const paramID = params.id === 'new' ? null : Number(params.id);
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency, isCashboxEnabled },
    },
  } = useContext(SettingsContext);

  const {
    loadedServices,
    isServicesLoading,
    updateService,
    createService,
    deleteService,
  } = useServicesRequests();

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    setError,
    formState: { isDirty, isSubmitting },
  } = useForm<IFormValues>({ mode: 'onChange', defaultValues: DEFAULT_VALUES });

  const currentService = loadedServices.find((ser) => ser.id === paramID);

  useEffect(() => {
    if (currentService) {
      reset({
        ...DEFAULT_VALUES,
        title: currentService.title,
        cost: currentService.cost,
        use_global_discounts: currentService.use_global_discounts,
        ean: currentService.ean,
        nds: currentService.vat,
      });

      trigger();

      return;
    }

    reset(DEFAULT_VALUES);
  }, [currentService, reset, trigger]);

  return (
    <div className={s.container}>
      <div className={s.link} onClick={() => navigate('/services')}>
        <BackwardIcon />
        {t('back')}
      </div>
      <Preloader loading={isServicesLoading}>
        <H1 className={s.header}>
          {t(paramID ? 'service.editing' : 'service.creating')}
        </H1>
        <div className={s.data}>
          <form
            className={s.form}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
            onSubmit={handleSubmit(({ cost, ...data }) => {
              if (paramID) {
                return updateService({
                  ...data,
                  id: paramID,
                  cost: cost || 0,
                  NDS: data.nds ?? undefined,
                })
                  .then(() => {
                    reset({ ...DEFAULT_VALUES });
                    navigate('/services');
                  })
                  .catch((error) => {
                    let message = t('errors.common');

                    if (error.message === 'ean is already taken') {
                      message = t('errors.ean.inUse');
                      setError('ean', { message });
                    }

                    if (error.message === 'service is used in combo') {
                      message = t('service.combo.error');
                    }

                    notificationService.info({
                      t,
                      type: 'error',
                      title: t('service.update.error'),
                      message,
                    });
                  });
              }

              return createService({
                ...data,
                cost: cost || 0,
                NDS: data.nds ?? undefined,
              })
                .then(() => {
                  reset({ ...DEFAULT_VALUES });
                  navigate('/services');
                })
                .catch((error) => {
                  let message = t('errors.common');

                  if (error.message === 'ean is already taken') {
                    message = t('errors.ean.inUse');
                    setError('ean', { message });
                  }

                  notificationService.info({
                    t,
                    type: 'error',
                    title: t('service.create.error'),
                    message,
                  });
                });
            })}
          >
            <div className={s.block}>
              <H2>{t('goods.edit.subtitle_basic')}</H2>
              <div className={s.fields}>
                <div className={s.field}>
                  <H3>{t('name')}</H3>
                  <Controller
                    render={({ field, fieldState: { error } }) => (
                      <Input
                        {...field}
                        placeholder={t('name.input')}
                        error={error?.message}
                        variant="secondary"
                        bordered
                      />
                    )}
                    name="title"
                    control={control}
                    rules={{
                      required: t('errors.required'),
                      minLength: {
                        value: 2,
                        message: t('errors.minLength', 2),
                      },
                      maxLength: {
                        value: 50,
                        message: t('errors.maxLength', 50),
                      },
                    }}
                  />
                </div>

                <div className={s.field}>
                  <H3>
                    {t('cost')}, {currency.sign}
                  </H3>
                  <Controller
                    render={({ field, fieldState: { error } }) => (
                      <NumberInput
                        {...field}
                        variant="secondary"
                        error={error?.message}
                        placeholder={t('write.cost')}
                        bordered
                      />
                    )}
                    name="cost"
                    control={control}
                    rules={{
                      required: t('errors.required'),
                      min: {
                        value: 0,
                        message: t('errors.min', 0, currency.sign),
                      },
                      max: {
                        value: 16000000,
                        message: t('errors.max', '16 000 000', currency.sign),
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            <div className={s.block}>
              <H2>{t('additional')}</H2>

              <div className={s.fields}>
                <div className={s.field}>
                  <Controller
                    render={({ field }) => (
                      <Toggle
                        {...field}
                        label={t('tariff.discount.apply')}
                        emptyBg
                        variant="secondary"
                      />
                    )}
                    name="use_global_discounts"
                    control={control}
                  />
                </div>
                {isCashboxEnabled && (
                  <div className={s.field}>
                    <H3>{t('goods.edit.nds.label')}</H3>
                    <Controller
                      render={({ field }) => (
                        <NDSSelect {...field} notBordered={false} />
                      )}
                      name="nds"
                      control={control}
                    />
                  </div>
                )}
                <div className={s.field}>
                  <div className={s.ean_top}>
                    <H3>{t('ean')}</H3>
                    <span>{t('ean.help')}</span>
                  </div>
                  <Controller
                    control={control}
                    render={({
                      field: { onChange, ...field },
                      fieldState: { error },
                    }) => (
                      <Input
                        {...field}
                        pattern="\d*"
                        placeholder={t('ean')}
                        onChange={(value, event) => {
                          if (event.target.validity.valid) {
                            onChange(value);
                          }
                        }}
                        variant="secondary"
                        error={error?.message}
                        errorWrapperClassName={s.fullWidth}
                        bordered
                      />
                    )}
                    name="ean"
                    rules={{
                      pattern: {
                        value: /^\d{8,16}$/,
                        message: t('good.manager.eans.error.length'),
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            <div className={s.controls}>
              {paramID && currentService && (
                <Button
                  variant="secondary"
                  color="red"
                  onClick={() => {
                    openConfirmDialog(t, 'remove', {
                      title: t('service.deleting'),
                      message: t(
                        'service.delete.confirm',
                        currentService.title
                      ),
                    })
                      .then(({ status }) => {
                        if (status === 'ACCEPTED') {
                          return deleteService(paramID).then(() =>
                            navigate('/services')
                          );
                        }
                      })
                      .catch(() => {
                        notificationService.info({
                          t,
                          type: 'error',
                          title: t('service.delete.errors.title'),
                          message: t('service.combo.error'),
                        });
                      });
                  }}
                  fullWidth
                >
                  {t('delete')}
                </Button>
              )}
              <Button
                type="submit"
                isLoading={isSubmitting}
                disabled={!isDirty}
                fullWidth
                className={!paramID ? s.button_one : ''}
              >
                {t(paramID ? 'save' : 'services.add')}
              </Button>
            </div>
          </form>
        </div>
      </Preloader>
    </div>
  );
};
